import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Input,
  InputNumber,
  Button,
  Space,
  Popconfirm,
  message,
  Spin,
  Divider,
  Select,
  DatePicker,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  getBpjsKaryawan,
  bulkInsertBpjs,
  bulkUpdateBpjs,
  bulkDeleteBpjs,
} from "../../api/transaksi/Bpjs";

import dayjs from "dayjs";

const BpjsPage = () => {
  const { Option } = Select;
  const [data, setData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingRow, setLoadingRow] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [bulan, setBulan] = useState(dayjs());
  const [isBulanValid, setIsBulanValid] = useState(true);

  useEffect(() => {
    loadData();
  }, [bulan]);

  const loadData = async () => {
    setLoadingTable(true);
    try {
      const apiData = await getBpjsKaryawan(bulan.format("YYYY-MM"));
      if (apiData.status) {
        setData(
          apiData.data.map((item, index) => ({
            key: index,
            ...item,
            bp_status: item.bp_status || "Tidak Aktif",
          }))
        );
      } else {
        message.error(apiData.message);
      }
    } catch (error) {
      console.error(error);
      message.error("Gagal mengambil data BPJS.");
    } finally {
      setLoadingTable(false);
    }
  };

  const handleChange = (val, key, field) => {
    setData((d) =>
      d.map((r) => {
        if (r.key === key) {
          const updatedRow = { ...r, [field]: val };

          // Hitung ulang total iuran
          const {
            bp_jkk = 0,
            bp_jkm = 0,
            bp_kesehatan_pegawai = 0,
            bp_kesehatan_perusahaan = 0,
            bp_jht_pegawai = 0,
            bp_jht_perusahaan = 0,
            bp_jp_pegawai = 0,
            bp_jp_perusahaan = 0,
          } = updatedRow;

          const total_bpjs =
            Number(bp_jkk || 0) +
            Number(bp_jkm || 0) +
            Number(bp_jht_pegawai || 0) +
            Number(bp_jht_perusahaan || 0) +
            Number(bp_jp_pegawai || 0) +
            Number(bp_jp_perusahaan || 0);

          const total_kesehatan =
            Number(bp_kesehatan_pegawai || 0) +
            Number(bp_kesehatan_perusahaan || 0);

          updatedRow.bp_total_iuran_bpjs = total_bpjs;
          updatedRow.bp_total_iuran_kesehatan = total_kesehatan;

          return updatedRow;
        }
        return r;
      })
    );
  };

  const handleSaveRow = async (record) => {
    setLoadingRow(record.key);
    try {
      const payload = {
        ...record,
        bp_employe_id: record.emp_id_karyawan,
        bp_periode: bulan.format("YYYY-MM"),
      };

      if (record.bp_id) {
        await bulkUpdateBpjs([{ ...payload, bp_updated_by: "admin" }]);
      } else {
        await bulkInsertBpjs([{ ...payload, bp_created_by: "admin" }]);
      }
      message.success(`Data BPJS berhasil disimpan`);
      loadData();
    } catch (err) {
      message.error("Gagal menyimpan data BPJS");
    } finally {
      setLoadingRow(null);
    }
  };

  const handleDeleteRow = async (record) => {
    setDeletingRow(record.key);
    try {
      await bulkDeleteBpjs([record.bp_id]);
      setData((prev) => prev.filter((r) => r.key !== record.key));
      message.success("Data berhasil dihapus");
    } catch (err) {
      message.error("Gagal menghapus data");
    } finally {
      setDeletingRow(null);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Cari
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleSearch = (keys, confirm, dataIndex) => {
    confirm();
    setSearchText(keys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const isValidMonth = (val) => {
    if (!val) return false;
    const now = dayjs();
    const lastMonth = now.subtract(1, "month");
    return val.isSame(now, "month") || val.isSame(lastMonth, "month");
  };

  const handleMonthChange = (val) => {
    setBulan(val);
    const valid = isValidMonth(val);
    setIsBulanValid(valid);
    // if (!valid) {
    //   message.warning("Absensi hanya untuk bulan ini atau bulan sebelumnya.");
    // }
  };
  const columns = [
    {
      title: "Nama Karyawan",
      dataIndex: "emp_nama",
      key: "emp_nama",
      ...getColumnSearchProps("emp_nama"),
      fixed: "left",
      width: 200,
    },
    {
      title: "JKK",
      dataIndex: "bp_jkk",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_jkk")}
        />
      ),
    },
    {
      title: "JKM",
      dataIndex: "bp_jkm",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_jkm")}
        />
      ),
    },
    {
      title: "JHT (Pegawai)",
      dataIndex: "bp_jht_pegawai",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_jht_pegawai")}
        />
      ),
    },
    {
      title: "JHT (Perusahaan)",
      dataIndex: "bp_jht_perusahaan",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_jht_perusahaan")}
        />
      ),
    },
    {
      title: "JP (Pegawai)",
      dataIndex: "bp_jp_pegawai",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_jp_pegawai")}
        />
      ),
    },
    {
      title: "JP (Perusahaan)",
      dataIndex: "bp_jp_perusahaan",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_jp_perusahaan")}
        />
      ),
    },
    {
      title: "Total Iuran Bpjs Ketenagakerjaan",
      dataIndex: "bp_total_iuran_bpjs",
      width: 80,
      render: (val) => (val ? `Rp ${Number(val).toLocaleString("id-ID")}` : ""),
    },
    {
      title: "Kesehatan (Pegawai)",
      dataIndex: "bp_kesehatan_pegawai",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleChange(v, record.key, "bp_kesehatan_pegawai")}
        />
      ),
    },
    {
      title: "Kesehatan (Perusahaan)",
      dataIndex: "bp_kesehatan_perusahaan",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) =>
            handleChange(v, record.key, "bp_kesehatan_perusahaan")
          }
        />
      ),
    },
    {
      title: "Total Iuran Bpjs Kesehatan",
      dataIndex: "bp_total_iuran_kesehatan",
      width: 150,
      render: (val) => (val ? `Rp ${Number(val).toLocaleString("id-ID")}` : ""),
    },
    {
      title: "Keterangan",
      dataIndex: "bp_deskripsi",
      render: (val, record) => (
        <Input
          value={val}
          onChange={(e) =>
            handleChange(e.target.value, record.key, "bp_deskripsi")
          }
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "bp_status",
      render: (val, record) => (
        <Select
          value={val}
          style={{ width: 120 }}
          onChange={(v) => handleChange(v, record.key, "bp_status")}
        >
          <Option value="Aktif">Aktif</Option>
          <Option value="Tidak Aktif">Tidak Aktif</Option>
        </Select>
      ),
    },
    {
      title: "Aksi",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleSaveRow(record)}
            loading={loadingRow === record.key}
          >
            Simpan
          </Button>
          <Popconfirm
            title="Yakin hapus data ini?"
            onConfirm={() => handleDeleteRow(record)}
            okText="Ya"
            cancelText="Batal"
          >
            <Button danger type="link" loading={deletingRow === record.key}>
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "0 24px 24px" }}>
      <Divider>Data BPJS Karyawan</Divider>
      <Space style={{ marginBottom: 16 }}>
        <label>Pilih Bulan: </label>
        <DatePicker
          picker="month"
          value={bulan}
          onChange={handleMonthChange}
          allowClear={false}
        />
        <Button
          type="primary"
          onClick={async () => {
            setLoadingAll(true);
            const toInsert = data.filter((d) => !d.bp_id);
            const toUpdate = data.filter((d) => d.bp_id);

            try {
              if (toInsert.length) {
                await bulkInsertBpjs(
                  toInsert.map((d) => ({
                    ...d,
                    bp_created_by: "admin",
                    bp_employe_id: d.emp_id_karyawan,
                    bp_periode: bulan.format("YYYY-MM"),
                  }))
                );
              }
              if (toUpdate.length) {
                await bulkUpdateBpjs(
                  toUpdate.map((d) => ({
                    ...d,
                    bp_updated_by: "admin",
                    bp_employe_id: d.emp_id_karyawan,
                    bp_periode: bulan.format("YYYY-MM"),
                  }))
                );
              }
              message.success("Data BPJS berhasil disimpan semua");
              loadData();
            } catch (error) {
              message.error("Gagal menyimpan semua data");
            } finally {
              setLoadingAll(false);
            }
          }}
          loading={loadingAll}
        >
          Simpan Semua
        </Button>

        <Popconfirm
          title="Yakin hapus semua data BPJS?"
          onConfirm={async () => {
            setDeletingAll(true);
            try {
              const ids = data.map((d) => d.bp_id).filter(Boolean);
              if (ids.length) {
                await bulkDeleteBpjs(ids);
                setData([]);
                message.success("Semua data berhasil dihapus");
              }
            } catch (err) {
              message.error("Gagal menghapus semua data");
            } finally {
              setDeletingAll(false);
            }
          }}
          okText="Ya"
          cancelText="Batal"
        >
          <Button danger loading={deletingAll}>
            Hapus Semua
          </Button>
        </Popconfirm>
      </Space>

      <Spin spinning={loadingTable}>
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          bordered
          rowKey="key"
          scroll={{ x: "max-content" }}
        />
      </Spin>
    </div>
  );
};

export default BpjsPage;
